import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MessageCircle, Filter, Lock, ExternalLink, Hash } from 'lucide-react';
import { dialogsApi, type DialogItem } from '../api/dialogs';
import { ticketsApi, type TicketBrief } from '../api/tickets';
import { fmtTime } from '../lib/format';
import { linkify } from '../lib/linkify';
import AttachmentPreviewModal from './AttachmentPreviewModal';

interface Props { orgId: string; }

type SourceFilter = 'all' | 'ticket' | 'max';

export default function OrgDialogsTab({ orgId }: Props) {
  const [items, setItems] = useState<DialogItem[]>([]);
  const [tickets, setTickets] = useState<TicketBrief[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [source, setSource] = useState<SourceFilter>('all');
  const [ticketId, setTicketId] = useState<string>('');
  const [author, setAuthor] = useState<string>('');
  const [includeInternal, setIncludeInternal] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const list = await dialogsApi.list(orgId, {
        source,
        ticket_id: ticketId || undefined,
        include_internal: includeInternal,
        limit: 500,
      });
      setItems(list);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  // Загрузить тикеты для фильтра «по тикету» один раз
  useEffect(() => {
    ticketsApi.list({ organization_id: orgId, limit: 500 }).then(r => setTickets(r.items)).catch(() => {});
  }, [orgId]);

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ },
            [orgId, source, ticketId, includeInternal]);

  const authors = useMemo(() => {
    const set = new Set<string>();
    for (const it of items) if (it.author_name) set.add(it.author_name);
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (author && it.author_name !== author) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(it.body?.toLowerCase().includes(s) ||
              it.author_name?.toLowerCase().includes(s) ||
              it.ticket_subject?.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [items, author, search]);

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white flex items-center gap-2">
          <MessageCircle size={16} className="text-blue-400" />
          Диалоги — {filtered.length}
        </h2>
      </header>

      {/* фильтры */}
      <div className="px-5 py-3 border-b border-slate-800 flex flex-wrap gap-2 items-center">
        <Filter size={13} className="text-slate-500" />
        <select value={source} onChange={e => setSource(e.target.value as SourceFilter)}
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white">
          <option value="all">все источники</option>
          <option value="ticket">только из тикетов</option>
          <option value="max">только из MAX-чата</option>
        </select>
        <select value={ticketId} onChange={e => setTicketId(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white max-w-[260px] truncate">
          <option value="">все тикеты</option>
          {tickets.map(t => (
            <option key={t.id} value={t.id}>SD-{String(t.number).padStart(4,'0')} · {t.subject}</option>
          ))}
        </select>
        {authors.length > 0 && (
          <select value={author} onChange={e => setAuthor(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white max-w-[200px] truncate">
            <option value="">все авторы</option>
            {authors.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Поиск по тексту…"
          className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white flex-1 min-w-[180px]" />
        <label className="text-xs text-slate-300 flex items-center gap-1.5">
          <input type="checkbox" checked={includeInternal}
                 onChange={e => setIncludeInternal(e.target.checked)} />
          включая внутренние
        </label>
      </div>

      <div className="p-4">
        {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm mb-3">{err}</div>}
        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-6" />
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-8">
            Нет реплик по выбранным фильтрам.
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map(it => <DialogRow key={`${it.source}-${it.id}`} item={it} />)}
          </ul>
        )}
      </div>
    </section>
  );
}

function DialogRow({ item }: { item: DialogItem }) {
  const [previewId, setPreviewId] = useState<string | null>(null);
  const isTicket = item.source === 'ticket';
  const author = item.author_name || item.author_email || '—';
  return (
    <li className="bg-slate-950/50 border border-slate-800 rounded-lg px-3 py-2.5">
      <header className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`shrink-0 inline-block px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border ${
            isTicket ? 'border-blue-700 text-blue-300 bg-blue-900/20'
                     : 'border-purple-700 text-purple-300 bg-purple-900/20'
          }`}>{isTicket ? 'тикет' : 'MAX'}</span>
          <span className="text-xs text-white font-medium truncate">{author}</span>
          {item.is_internal && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-amber-400">
              <Lock size={10} /> внутр.
            </span>
          )}
        </div>
        <span className="shrink-0 text-[10px] text-slate-500 font-mono">{fmtTime(item.posted_at)}</span>
      </header>

      {isTicket && item.ticket_id && (
        <div className="text-[11px] text-slate-400 mb-1 flex items-center gap-1">
          <Hash size={10} className="text-slate-500" />
          <Link to={`/tickets/${item.ticket_id}`}
                className="text-blue-400 hover:underline truncate">
            SD-{String(item.ticket_number ?? 0).padStart(4,'0')} · {item.ticket_subject}
          </Link>
          <ExternalLink size={9} className="text-slate-500" />
        </div>
      )}

      <p className="text-sm text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
        {linkify(item.body, (id) => setPreviewId(id))}
      </p>
      {previewId && (
        <AttachmentPreviewModal attachmentId={previewId}
          onClose={() => setPreviewId(null)} />
      )}
    </li>
  );
}
